import { Card, Descriptions, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { SettlementRoot2 } from '@repo/sdk'

import { StatusBadge } from '../../components/StatusBadge'
import { formatKrw } from '@repo/shared'
import { useSettlementStatusConfig } from './settlementStatusConfig'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

const { Text } = Typography

interface SettlementInfoCardProps {
  settlement: SettlementRoot2
}

export function SettlementInfoCard({ settlement }: SettlementInfoCardProps) {
  const { t } = useTranslation()
  const statusConfig = useSettlementStatusConfig()

  return (
    <Card title={t('pages.settlements.sections.settlementInfo')} bordered>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('common.columns.settlementId')}>
          <Text data-testid="settlement-detail-id-copy" copyable>
            {settlement.settlementId}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('common.columns.status')}>
          <StatusBadge status={settlement.status} configMap={statusConfig} />
        </Descriptions.Item>
        {settlement.status === 'FAILED' && settlement.failureReason && (
          <Descriptions.Item label={t('common.columns.failedReason')}>
            <Text type="danger">{settlement.failureReason}</Text>
          </Descriptions.Item>
        )}
        {settlement.status === 'COMPLETED' && settlement.paymentAgencyTransactionId && (
          <Descriptions.Item label={t('common.columns.transactionId')}>
            <Text data-testid="settlement-detail-transaction-id-copy" copyable>
              {settlement.paymentAgencyTransactionId}
            </Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('common.columns.totalPaymentAmount')}>
          {formatKrw(settlement.totalPaymentAmount.amount)}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.columns.platformFee')}>
          -{formatKrw(settlement.platformCommission.amount)}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.columns.grossPayoutAmount')}>
          <Text strong>{formatKrw(settlement.payoutAmount.amount)}</Text>
        </Descriptions.Item>
        {settlement.netPayoutAmount && (
          <Descriptions.Item label={t('common.columns.netPayoutAmount')}>
            <Text strong>{formatKrw(settlement.netPayoutAmount.amount)}</Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t('common.columns.eligibilityDate')}>
          {formatKstDateTime(settlement.eligibilityDate, 'YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.columns.createdAt')}>
          {formatKstDateTime(settlement.createdAt, 'YYYY-MM-DD HH:mm')}
        </Descriptions.Item>
        {settlement.confirmedAt && (
          <Descriptions.Item label={t('common.columns.confirmedAt')}>
            {formatKstDateTime(settlement.confirmedAt, 'YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        )}
        {settlement.paidAt && (
          <Descriptions.Item label={t('common.columns.paidOutAt')}>
            {formatKstDateTime(settlement.paidAt, 'YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  )
}
